import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function ForgotPassword() {
  const { t } = useTranslation('app');
  const [wellisId, setWellisId] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateWellisId = (id) => {
    // Accepts both legacy 8-char and current 10-char Wellis IDs, since both
    // exist among registered accounts.
    const wellisIdRegex = /^WELLIS-[0-9A-Za-z]{8}([0-9A-Za-z]{2})?$/;
    return wellisIdRegex.test(id.toUpperCase());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedId = wellisId.toUpperCase().trim();
    if (!trimmedId) {
      setError(t('auth.forgotPassword.noWellisId'));
      return;
    }

    if (!validateWellisId(trimmedId)) {
      setError(t('auth.forgotPassword.invalidWellisId'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ wellis_id: trimmedId }),
      });

      const contentType = response.headers.get('content-type');
      const data = contentType?.includes('application/json')
        ? await response.json()
        : null;

      if (!response.ok) {
        throw new Error(data?.error ?? `Request failed (${response.status}). Please try again.`);
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-margin-mobile pt-20">
        <div className="w-full max-w-md text-center">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/10 mb-4">
              <span className="material-symbols-outlined text-success text-headline-lg">check_circle</span>
            </div>
            <h1 className="text-headline-lg text-on-background mb-3">{t('auth.forgotPassword.checkEmail')}</h1>
            <p className="text-body-md text-on-background/60">
              {t('auth.forgotPassword.emailSent')}
            </p>
          </div>

          <div className="bg-primary-container/30 border border-primary/20 rounded-lg p-6 mb-8 text-left">
            <p className="text-body-sm text-on-background/70 mb-3">{t('auth.forgotPassword.linkExpiry')}</p>
            <p className="text-body-sm text-on-background/70">{t('auth.forgotPassword.noEmailReceived')}</p>
          </div>

          <Link
            to="/login"
            className="inline-block text-primary font-medium hover:underline text-body-md"
          >
            {t('auth.forgotPassword.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-margin-mobile">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <h1 className="text-headline-lg text-on-background mb-2">{t('auth.forgotPassword.title')}</h1>
          <p className="text-body-md text-on-background/60">{t('auth.forgotPassword.subtitle')}</p>
        </div>

        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-6">
          <p className="text-body-sm text-on-background/80">
            <strong>{t('auth.forgotPassword.memberOnly')}</strong> — {t('auth.forgotPassword.memberOnlyDesc')}
          </p>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error p-4 rounded-lg mb-6 text-body-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="wellis_id" className="block text-body-sm text-on-background mb-1">
              {t('auth.forgotPassword.wellisIdLabel')}
            </label>
            <input
              id="wellis_id"
              type="text"
              value={wellisId}
              onChange={(e) => setWellisId(e.target.value.toUpperCase())}
              className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-surface text-body-sm focus:outline-none focus:border-primary"
              placeholder={t('auth.forgotPassword.wellisIdPlaceholder')}
              autoComplete="off"
            />
            <p className="text-body-xs text-on-background/60 mt-1">
              {t('auth.forgotPassword.wellisIdHelp')}
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-on-primary font-medium py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 text-body-md"
          >
            {loading ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.sendButton')}
          </button>
        </form>

        <p className="text-body-sm text-on-background/60 text-center mt-6">
          {t('auth.forgotPassword.rememberPassword')}{' '}
          <Link to="/login" className="text-primary font-medium hover:underline">
            {t('auth.forgotPassword.backToLogin')}
          </Link>
        </p>
      </div>
    </div>
  );
}
